import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface CarouselSprite {
  url: string
  prompt?: string
  isLoading?: boolean
  id?: string
}

interface SpriteGeneratorProps {
  onSpriteGenerated?: (imageUrl: string) => void;
}

export function SpriteGenerator({ onSpriteGenerated }: SpriteGeneratorProps) {
  const [prompt, setPrompt] = useState('')
  const [width, setWidth] = useState(768)
  const [height, setHeight] = useState(768)
  const [numInferenceSteps, setNumInferenceSteps] = useState(20)
  const [guidanceScale, setGuidanceScale] = useState(7)
  const [seed, setSeed] = useState(-1)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [generatedSprites, setGeneratedSprites] = useState<CarouselSprite[]>([ ])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    // Generate a temporary ID for this generation
    const tempId = Math.random().toString(36).substring(7)
    
    // Add a loading placeholder to the carousel
    setGeneratedSprites(prev => [{
      url: '',
      prompt,
      isLoading: true,
      id: tempId
    }, ...prev])
    
    try {
      const response = await fetch('/api/v1/sprites/new/png?inline=true', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          width,
          height,
          steps: numInferenceSteps,
          cfg_scale: guidanceScale,
          seed: seed >= 0 ? seed : undefined,
          negative_prompt: '',
          styles: [],
        }),
      })

      if (!response.ok) {
        // Handle error responses
        if (response.status === 400) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Invalid parameters')
        } else if (response.status === 404) {
          throw new Error('Sprite not found')
        } else {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      }

      // Handle successful responses
      if (response.status === 302 || response.redirected) {
        // Handle redirect case - the sprite is ready at the redirected URL
        const redirectUrl = response.url
        const uuid = redirectUrl.split('/').slice(-2)[0] // Extract UUID from redirect URL
        
        // Fetch the actual sprite
        const spriteResponse = await fetch(`/api/v1/sprites/${uuid}/png`)
        if (!spriteResponse.ok) {
          if (spriteResponse.status === 404) {
            throw new Error('Generated sprite not found')
          }
          throw new Error('Failed to fetch generated sprite')
        }
        
        const imageBlob = await spriteResponse.blob()
        const imageUrl = URL.createObjectURL(imageBlob)
        
        if (onSpriteGenerated) {
          onSpriteGenerated(imageUrl)
        } else {
          setGeneratedSprites(prev => {
            const newSprites = [...prev]
            const loadingIndex = newSprites.findIndex(sprite => sprite.id === tempId)
            if (loadingIndex !== -1) {
              newSprites[loadingIndex] = {
                url: imageUrl,
                prompt,
                id: tempId
              }
            }
            return newSprites
          })
        }
      } else if (response.status === 200 || response.status === 201) {
        // Direct response case - the sprite data is in the response
        const imageBlob = await response.blob()
        const imageUrl = URL.createObjectURL(imageBlob)
        
        if (onSpriteGenerated) {
          onSpriteGenerated(imageUrl)
        } else {
          setGeneratedSprites(prev => {
            const newSprites = [...prev]
            const loadingIndex = newSprites.findIndex(sprite => sprite.id === tempId)
            if (loadingIndex !== -1) {
              newSprites[loadingIndex] = {
                url: imageUrl,
                prompt,
                id: tempId
              }
            }
            return newSprites
          })
        }
      } else {
        throw new Error('Unexpected response status')
      }
    } catch (err) {
      console.error('Sprite generation error:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate sprite')
      // Remove the loading placeholder on error
      setGeneratedSprites(prev => prev.filter(sprite => sprite.id !== tempId))
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-xl shadow-sm">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="prompt" className="block text-sm font-medium text-gray-700">
            Describe your sprite
          </label>
          <div className="mt-1">
            <input
              type="text"
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="A magical crystal in pixel art style"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="width" className="block text-sm font-medium text-gray-700">
              Width
            </label>
            <div className="mt-1">
              <input
                type="number"
                id="width"
                value={width}
                onChange={(e) => {
                  const value = Number(e.target.value)
                  // Round to nearest multiple of 64
                  setWidth(Math.round(value / 64) * 64)
                }}
                min="64"
                step="64"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Must be a multiple of 64</p>
            </div>
          </div>

          <div>
            <label htmlFor="height" className="block text-sm font-medium text-gray-700">
              Height
            </label>
            <div className="mt-1">
              <input
                type="number"
                id="height"
                value={height}
                onChange={(e) => {
                  const value = Number(e.target.value)
                  // Round to nearest multiple of 64
                  setHeight(Math.round(value / 64) * 64)
                }}
                min="64"
                step="64"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Must be a multiple of 64</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="numInferenceSteps" className="block text-sm font-medium text-gray-700">
              Inference Steps
            </label>
            <input
              type="number"
              id="numInferenceSteps"
              value={numInferenceSteps}
              onChange={(e) => setNumInferenceSteps(Number(e.target.value))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="guidanceScale" className="block text-sm font-medium text-gray-700">
              Guidance Scale
            </label>
            <input
              type="number"
              id="guidanceScale"
              value={guidanceScale}
              onChange={(e) => setGuidanceScale(Number(e.target.value))}
              step="0.1"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="seed" className="block text-sm font-medium text-gray-700">
              Seed
            </label>
            <input
              type="number"
              id="seed"
              value={seed}
              onChange={(e) => setSeed(Number(e.target.value))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? 'Generating...' : 'Generate Sprite'}
          </button>
        </div>
      </form>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-4 p-4 bg-red-50 text-red-700 rounded-md"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Generation Error</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
                <p className="mt-2 text-xs text-red-600">Please try again or adjust your parameters.</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Sprite Carousel */}
        <div className="mt-12">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Generated Sprites Gallery</h3>
          <div className="grid grid-cols-4 gap-4">
            {generatedSprites.map((sprite, index) => (
              <motion.div
                key={sprite.id || index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="relative w-full pt-[100%] group"
              >
                <div className="absolute inset-0">
                  {sprite.isLoading ? (
                    <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse">
                      <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-[shimmer_1.5s_infinite]" 
                           style={{
                             backgroundSize: '200% 100%',
                             animation: 'shimmer 1.5s infinite linear',
                           }}
                      />
                    </div>
                  ) : (
                    <img
                      src={sprite.url}
                      alt={sprite.prompt || 'Generated sprite'}
                      className="object-contain transition-transform group-hover:scale-110"
                    />
                  )}
                </div>
                {sprite.prompt && !sprite.isLoading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                    <p className="text-white text-xs truncate">{sprite.prompt}</p>
                  </div>
                )}
                {sprite.isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-sm text-gray-500">Generating...</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatePresence>
    </div>
  )
} 